import { useState, useCallback } from 'react';

/**
 * Custom hook for managing bento grid item selection state
 * @param {Array<string>} [initialSelected] - Initially selected item IDs
 * @returns {Object} - Selection state and handlers
 */
export const useBentoSelection = (initialSelected = []) => {
  const [selected, setSelected] = useState(new Set(initialSelected));

  const toggleItem = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectItem = useCallback((id) => {
    setSelected(prev => new Set([...prev, id]));
  }, []);

  const deselectItem = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((allIds) => {
    setSelected(new Set(allIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const isSelected = useCallback((id) => {
    return selected.has(id);
  }, [selected]);

  return {
    selected,
    toggleItem,
    selectItem,
    deselectItem,
    selectAll,
    clearSelection,
    isSelected
  };
};

